import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const isEmailAddress = (value) => (
  typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value?.trim())
);

const formatEmailUsername = (email) => {
  const username = typeof email === 'string' ? email?.split('@')?.[0] : '';
  const readable = username
    ?.replace(/[._-]+/g, ' ')
    ?.replace(/\b\w/g, character => character?.toUpperCase())
    ?.trim();
  return readable || 'User';
};

const resolveDisplayName = ({ authUser, userProfile, impersonationData }) => {
  const metadata = authUser?.user_metadata || {};
  const metadataParts = [metadata?.first_name, metadata?.last_name]
    ?.filter(value => typeof value === 'string' && value?.trim())
    ?.join(' ');
  const candidates = impersonationData
    ? [impersonationData?.targetName]
    : [
        userProfile?.full_name,
        metadata?.full_name,
        metadata?.name,
        metadata?.display_name,
        metadataParts,
      ];
  const displayName = candidates?.find(value => (
    typeof value === 'string' && value?.trim() && !isEmailAddress(value)
  ));
  const email = impersonationData?.targetEmail || authUser?.email;
  return displayName?.trim() || formatEmailUsername(email);
};

const UserAccountDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user: authUser, signOut, isAdmin, isOwner, userProfile, getAccountLevel } = useAuth();

  const isSuperAdmin = isAdmin || isOwner || (authUser?.email && authUser?.email === import.meta.env?.VITE_SUPER_ADMIN_EMAIL);

  // Check if currently impersonating a user
  const impersonationData = (() => {
    try {
      const stored = sessionStorage?.getItem('superAdminImpersonating');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  const user = {
    name: resolveDisplayName({ authUser, userProfile, impersonationData }),
    email: impersonationData ? impersonationData?.targetEmail : (authUser?.email || ''),
    avatar: null,
    plan: impersonationData
      ? (impersonationData?.targetPlan || 'free')
      : (getAccountLevel ? getAccountLevel() : (isAdmin || isOwner ? 'Owner' : 'Client'))
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      closeDropdown();
      toast?.success('Logged out successfully');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast?.error('Failed to logout. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        closeDropdown();
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-smooth"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-primary">
            {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
          </span>
        </div>
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-foreground">{user?.name}</div>
          <div className="text-xs text-muted-foreground">{user?.plan}</div>
        </div>
        <Icon 
          name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
          size={16} 
          color="var(--color-muted-foreground)" 
        />
      </button>
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-md shadow-elevation-lg z-[1010] animate-slide-down"
        >
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                <span className="text-base font-medium text-primary">
                  {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-popover-foreground truncate">
                  {user?.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {user?.email}
                </div>
              </div>
            </div>
          </div>

          <div className="p-2">
            <Link
              to="/account-settings"
              onClick={closeDropdown}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-popover-foreground hover:bg-muted transition-smooth"
            >
              <Icon name="Settings" size={18} color="var(--color-muted-foreground)" />
              <span>Account Settings</span>
            </Link>

            <button
              onClick={closeDropdown}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-popover-foreground hover:bg-muted transition-smooth"
            >
              <Icon name="CreditCard" size={18} color="var(--color-muted-foreground)" />
              <span>Billing & Plan</span>
              <span className="ml-auto text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                {user?.plan}
              </span>
            </button>

            <Link
              to="/help-center-hub"
              onClick={closeDropdown}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-popover-foreground hover:bg-muted transition-smooth"
            >
              <Icon name="HelpCircle" size={18} color="var(--color-muted-foreground)" />
              <span>Help & Support</span>
            </Link>
          </div>

          <div className="p-2 border-t border-border">
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-destructive hover:bg-destructive/10 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name={isLoggingOut ? "Loader2" : "LogOut"} size={18} color="var(--color-destructive)" className={isLoggingOut ? "animate-spin" : ""} />
              <span>{isLoggingOut ? 'Logging out...' : 'Log Out'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccountDropdown;